import React, { useEffect, useState } from "react";
import { StyledTable, TableHeader, TitleContainer } from "../../../style";
import {
  Checkbox,
  IconButton,
  InputAdornment,
  TableCell,
  TextField,
  Typography,
} from "@mui/material";
import ColumnContainer from "../../../../common/ColumnContainer";
import RowContainer from "../../../../common/RowContainer";
import { DashboardButton } from "../../../../common/DashboardButton";
import { Add, KeyboardArrowDownRounded, Remove } from "@mui/icons-material";
import { SnackbarComponent } from "../../../../common/SnackbarComponent";
import {
  useGetCreditPricingTiersQuery,
  useUpdateCreditPricingTiersMutation,
} from "../../../../../redux/features/credits/creditsApi";
import { useDispatch, useSelector } from "react-redux";
import {
  getCreditsPricing,
  getStep,
  selectStep,
} from "../../../../../redux/features/credits/creditsSlice";
import { useAuth } from "../../../../../utils/hooks/useAuth";
import { FormattedMessage } from "react-intl";
import { SkeletonLoading } from "../../../../common/SkeletonLoading";
import { ErrorLoading } from "../../../../common/ErrorLoading";

const PricingCredits = () => {
  //INIT
  const dispatch = useDispatch();
  // REDUX
  const { auth } = useAuth();
  const adminId = auth?.userData?.role === "admin" && auth?.userData?.id;

  const { data, isLoading, isError } = useGetCreditPricingTiersQuery({
    adminId: adminId,
  });
  const [updatePricing] = useUpdateCreditPricingTiersMutation();
  const reduxStep = useSelector(selectStep);

  // EFFECTS
  useEffect(() => {
    if (data) {
      const mutableData = JSON.parse(JSON.stringify(data));
      setPricingCredits(mutableData);
      dispatch(getCreditsPricing(mutableData));
    } else {
      setPricingCredits([
        {
          id: 1,
          min_tier: 0,
          max_tier: 0,
          unit_price: 0,
          is_special: false,
        },
      ]);
    }
  }, [data, dispatch]);

  // STATE
  const [pricingCredits, setPricingCredits] = useState([
    {
      id: 1,
      min_tier: 0,
      max_tier: 0,
      unit_price: 0,
      is_special: false,
    },
  ]);

  const [step, setStep] = useState(reduxStep);
  const [openCard, setOpenCard] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [openSnackbarMaxTier, setOpenSnackbarMaxTier] = useState(false);
  const [openSnackbarPricing, setOpenSnackbarPricing] = useState(false);
  const [intervalId, setIntervalId] = useState(null);

  // UTILS
  const tableHeaders = [
    "N.",
    <FormattedMessage id="settings.listing.credits.min" />,
    <FormattedMessage id="settings.listing.credits.max" />,
    <FormattedMessage id="settings.listing.credits.price" />,
    <FormattedMessage id="settings.listing.credits.special" />,
  ];

  // FUNCTIONS
  const handleOpenCard = () => {
    setOpenCard(!openCard);
  };

  const renderPricingRows = () => {
    return pricingCredits.map((credit, index) => (
      <tr key={index}>
        <TableCell>{credit.id}</TableCell>
        <TableCell>
          {index === 0 ? (
            <RowContainer center align="center" className="counter-container">
              <Typography>0</Typography>
            </RowContainer>
          ) : (
            <RowContainer center align="center" className="counter-container">
              <Typography>
                {pricingCredits.find((item, i) => i === index - 1)?.max_tier +
                  1 || 0}
              </Typography>
            </RowContainer>
          )}
        </TableCell>
        <TableCell>
          <RowContainer align="center" className="counter-container">
            <IconButton
              onClick={() =>
                handleInputChange(
                  index,
                  "max_tier",
                  (credit?.max_tier || 0) - 1
                )
              }
              onMouseDown={() =>
                handleMouseDown(index, "max_tier", credit?.max_tier, "remove")
              }
              onMouseUp={() => handleMouseUp()}
              onMouseLeave={() => handleMouseUp()}
            >
              <Remove />
            </IconButton>
            <Typography>{credit?.max_tier || 0}</Typography>
            <IconButton
              onClick={() =>
                handleInputChange(
                  index,
                  "max_tier",
                  (credit?.max_tier || 0) + 1
                )
              }
              onMouseDown={() =>
                handleMouseDown(index, "max_tier", credit?.max_tier, "add")
              }
              onMouseUp={() => handleMouseUp()}
              onMouseLeave={() => handleMouseUp()}
            >
              <Add />
            </IconButton>
          </RowContainer>
        </TableCell>
        <TableCell>
          <TextField
            InputProps={{
              startAdornment: (
                <InputAdornment
                  position="start"
                  sx={{ "& p": { color: "var(--text-secondary)" } }}
                >
                  €
                </InputAdornment>
              ),
            }}
            type="number"
            value={credit?.unit_price || null}
            inputProps={{ min: 0, step: 0.01 }}
            onChange={(e) =>
              handleInputChange(index, "unit_price", e.target.value)
            }
          />
        </TableCell>

        <TableCell>
          <Checkbox
            checked={credit?.is_special}
            onChange={(e) =>
              handleInputChange(index, "is_special", e.target.checked)
            }
          />
        </TableCell>
        <TableCell>
          <DashboardButton type="outlined" onClick={() => removeRow(index)}>
            <FormattedMessage id="button.remove" />
          </DashboardButton>
        </TableCell>
      </tr>
    ));
  };

  const handleMouseDown = (index, field, value, action) => {
    const id = setInterval(() => {
      if (action === "remove") {
        handleInputChange(index, field, (value || 0) - 1);
        value -= 1;
      } else if (action === "add") {
        handleInputChange(index, field, (value || 0) + 1);
        value += 1;
      }
    }, 100);
    setIntervalId(id);
  };

  const handleMouseUp = () => {
    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
  };

  // const handleStepChange = (e) => {
  //   setStep(parseInt(e.target.value));
  // };

  const handleInputChange = (index, field, value) => {
    const newPricingCredits = pricingCredits.map((credit, i) => {
      if (i === index) {
        return {
          ...credit,
          [field]: Math.max(0, value),
          min_tier: i === 0 ? 0 : pricingCredits[i - 1].max_tier + 1,
        };
      } else if (i === index + 1) {
        return {
          ...credit,
          min_tier: pricingCredits[i - 1].max_tier + 1,
        };
      }
      return credit;
    });

    setPricingCredits(newPricingCredits);
  };

  const addRow = () => {
    let newTier = {
      id: 1,
      min_tier: 0,
      max_tier: 0,
      unit_price: 0,
      is_special: false,
    };
    if (pricingCredits.length === 0) {
      setPricingCredits([newTier]);
    } else {
      const lastTier = pricingCredits[pricingCredits.length - 1];
      newTier = {
        id: lastTier.id + 1,
        min_tier: lastTier.max_tier + 1,
        max_tier: 0,
        unit_price: 0,
        is_special: false,
      };
      setPricingCredits([...pricingCredits, newTier]);
    }
  };
  const removeRow = (index) => {
    if (index === 0) return;
    const updatedCredits = pricingCredits.filter((_, i) => i !== index);
    setPricingCredits(updatedCredits);
  };

  const saveChanges = () => {
    const updatedPricingCredits = pricingCredits.map((tier, index) => ({
      ...tier,
      min_tier: index === 0 ? 0 : pricingCredits[index - 1].max_tier + 1,
    }));

    const maxTiersCorrect = updatedPricingCredits.every(
      (tier) => Number(tier.max_tier) > Number(tier.min_tier)
    );
    const minTiersCorrect = updatedPricingCredits.every(
      (tier, index) =>
        index === 0 || tier.min_tier > updatedPricingCredits[index - 1].max_tier
    );
    const isPricingSet = updatedPricingCredits.every(
      (tier) => Number(tier.unit_price) > 0
    );

    if (maxTiersCorrect && minTiersCorrect && isPricingSet) {
      updatePricing({ adminId: adminId, tiers: updatedPricingCredits });
      dispatch(getCreditsPricing(updatedPricingCredits));
      dispatch(getStep(step));
    } else {
      if (!maxTiersCorrect) {
        setOpenSnackbarMaxTier(true);
      }
      if (!minTiersCorrect) {
        setOpenSnackbar(true);
      }
      if (!isPricingSet) {
        setOpenSnackbarPricing(true);
      }
    }
  };
  return (
    <ColumnContainer start>
      <TitleContainer>
        <RowContainer start align="center">
          <Typography variant="h5" component="h5">
            <FormattedMessage id="settings.listing.credits" />
          </Typography>
          <IconButton
            onClick={handleOpenCard}
            sx={{
              backgroundColor: "var(--bkg-quaternary)",
            }}
          >
            <KeyboardArrowDownRounded
              sx={{
                "& path": { color: "var(--main-color)" },
                cursor: "pointer",
                transform: openCard ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </IconButton>
        </RowContainer>
      </TitleContainer>

      {openCard ? (
        isLoading ? (
          <SkeletonLoading type="settings" />
        ) : isError ? (
          <ErrorLoading />
        ) : (
          <ColumnContainer
            start
            style={{ gap: "3rem" }}
            className="pricing-credits-page"
          >
            <RowContainer start>
              {/* <TextField
                sx={{ width: "50%" }}
                fullWidth={true}
                label="Step"
                type="number"
                value={step || null}
                onChange={(e) => handleStepChange(e)}
                helperText={
                  <Typography variant="p" color={"var(--main-color)"}>
                    L'utente potrà acquistare i crediti come multipli di questo
                    valore.
                  </Typography>
                }
              ></TextField> */}
            </RowContainer>
            <ColumnContainer>
              {pricingCredits?.length > 0 ? (
                <StyledTable>
                  <thead>
                    {tableHeaders.map((header, index) => (
                      <TableHeader key={index}>{header}</TableHeader>
                    ))}
                  </thead>
                  <tbody>{renderPricingRows()}</tbody>
                </StyledTable>
              ) : null}

              <RowContainer end minGap>
                <DashboardButton type="outlined" onClick={addRow}>
                  <FormattedMessage id="button.addCreditTier" />
                </DashboardButton>

                <DashboardButton type="filled" onClick={saveChanges}>
                  <FormattedMessage id="button.save" />
                </DashboardButton>
              </RowContainer>
            </ColumnContainer>
            <SnackbarComponent
              message={<FormattedMessage id="snackbar.correctValueStep" />}
              duration={7000}
              open={openSnackbarMaxTier}
              handleClose={() => setOpenSnackbarMaxTier(false)}
            ></SnackbarComponent>
            <SnackbarComponent
              message={<FormattedMessage id="snackbar.unitPrice" />}
              duration={7000}
              open={openSnackbarPricing}
              handleClose={() => setOpenSnackbarPricing(false)}
              className="snackbar-unit-price"
            ></SnackbarComponent>
          </ColumnContainer>
        )
      ) : null}
    </ColumnContainer>
  );
};

export default PricingCredits;
